<template>
  <div>
    <v-container fluid>
      <v-card class="reportdata-card-main">
        <v-card-title class="d-flex align-center justify-space-between px-0">
          <h3 class="text-h5 font-weight-bold" color="#171717">User Usage Report Management</h3>
        </v-card-title>
        <v-divider class="model-all-divider"></v-divider>

        <v-card-text class="pa-0">
          <div class="reportdata-filters-wrapper">
            <!-- Filter Row -->
            <div class="reportdata-filter-card">
              <div class="reportdata-filter-header">
                <img src="@/assets/material-symbols_arrow-range-rounded.svg" alt="Date range" width="20" height="20" />
                <span class="reportdata-filter-title">Date Range</span>
              </div>
              <v-row class="items-center mt-2">
                <!-- Start Date -->
                <v-col cols="12" sm="6" md="5" lg="4">
                  <div class="mb-1 d-flex align-center" style="gap: 2px">
                    <label for="Start Date" class="text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <v-tooltip location="top" max-width="230px">
                      <template v-slot:activator="{ props }">
                        <img v-bind="props" src="@/assets/intent group toolip.svg" alt="info" width="18" height="18"
                          style="cursor: pointer; margin-left: 4px;" />
                      </template>
                      <span>Select start date for filtering.</span>
                    </v-tooltip>
                  </div>
                  <v-menu v-model="startDateFlag" :close-on-content-click="false" transition="scale-transition"
                    location="bottom" min-width="auto">
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="startDate"
                        placeholder="Select date"
                        readonly
                        variant="outlined"
                        v-bind="props"
                        density="compact"
                        class="reportdata-field-date mt-1"
                        clearable
                        @click:clear="clearStartDate"
                        hide-details
                      >
                        <template #prepend-inner>
                          <img src="@/assets/Icons/new_calendar.png" alt="calendar" style="width: 20px; height: 20px;" />
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      :key="`start-${startPickerYear}-${startPickerMonth}`"
                      :year="startPickerYear"
                      :month="startPickerMonth"
                      @update:year="startPickerYear = $event"
                      @update:month="startPickerMonth = $event"
                      :max="new Date().toISOString().substr(0, 10)"
                      @update:model-value="
                        (val) => {
                          startDate = formatDate(val);
                          startDateFlag = false;
                        }
                      "
                      show-adjacent-months
                      class="date-picker">
                      <template #header>
                        <div class="picker-header-custom d-flex align-center justify-space-between px-4 py-3">
                          <div class="text-h6 font-weight-medium">{{ getFormattedHeaderDate(startPickerYear, startPickerMonth, startDate) }}</div>
                          <v-select
                            v-model="startPickerYear"
                            :items="years"
                            density="compact"
                            variant="plain"
                            hide-details
                            class="month-year-select"
                            style="width: 100px;"
                          ></v-select>
                        </div>
                      </template>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <div class="date-divider d-none d-lg-flex">-</div>

                <!-- End Date -->
                <v-col cols="12" sm="6" md="5" lg="4">
                  <div class="mb-1 d-flex align-center" style="gap: 2px">
                    <label for="End Date" class="text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <v-tooltip location="top" max-width="230px">
                      <template v-slot:activator="{ props }">
                        <img v-bind="props" src="@/assets/intent group toolip.svg" alt="info" width="18" height="18"
                          style="cursor: pointer; margin-left: 4px;" />
                      </template>
                      <span>Select end date for filtering.</span>
                    </v-tooltip>
                  </div>
                  <v-menu v-model="endDateFlag" :close-on-content-click="false" transition="scale-transition"
                    location="bottom" min-width="auto">
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="endDate"
                        placeholder="Select date"
                        readonly
                        variant="outlined"
                        v-bind="props"
                        density="compact"
                        class="reportdata-field-date mt-1"
                        clearable
                        @click:clear="clearEndDate"
                        hide-details
                      >
                        <template #prepend-inner>
                          <img src="@/assets/Icons/new_calendar.png" alt="calendar" style="width: 20px; height: 20px;" />
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      :key="`end-${endPickerYear}-${endPickerMonth}`"
                      :year="endPickerYear"
                      :month="endPickerMonth"
                      @update:year="endPickerYear = $event"
                      @update:month="endPickerMonth = $event"
                      :min="startDate != null ? startDate : null"
                      @update:model-value="
                        (val) => {
                          endDate = formatDate(val);
                          endDateFlag = false;
                        }
                      "
                      color="#C32E0E"
                      class="date-picker">
                      <template #header>
                        <div class="picker-header-custom d-flex align-center justify-space-between px-4 py-3">
                          <div class="text-h6 font-weight-medium">{{ getFormattedHeaderDate(endPickerYear, endPickerMonth, endDate) }}</div>
                          <v-select
                            v-model="endPickerYear"
                            :items="years"
                            density="compact"
                            variant="plain"
                            hide-details
                            class="month-year-select"
                            style="max-width: 90px;"
                          ></v-select>
                        </div>
                      </template>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Search Button -->
                <v-col cols="12" md="auto" class="d-flex justify-end align-end px-0">
                  <v-tooltip location="top" max-width="200">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon class="reportdata-icon-btn reportdata-icon-btn--primary mb-1"
                        variant="flat" @click="Search" aria-label="Search">
                        <v-icon size="22">mdi-magnify</v-icon>
                      </v-btn>
                    </template>
                    <span>Search with current filters</span>
                  </v-tooltip>
                </v-col>
                <!-- Quick Date Range Filters -->
                <v-col cols="12" md="12" lg="3" class="d-flex align-center justify-space-between ml-auto pl-0">
                  <v-divider vertical class="reportdata-filter-divider d-xl-flex"></v-divider>
                  <div class="reportdata-quick-section">
                    <div class="reportdata-quick-label mb-1">
                      <span>Quick Date Range</span>
                    </div>
                    <div class="reportdata-quick-controls">
                      <v-select v-model="selectedQuickRange" :items="quickDateRanges"
                        @update:model-value="onQuickRangeChange" variant="plain" density="compact"
                        class="reportdata-quick-select" hide-details
                        :menu-props="{ contentClass: 'quick-range-menu'}">
                        <template #prepend-inner>
                          <img src="@/assets/Icons/new_calendar_white.png" alt="calendar" width="20" height="20"
                            class="date-field-icon" />
                        </template>
                      </v-select>
                      <v-tooltip location="top" max-width="200">
                        <template #activator="{ props }">
                          <v-btn v-bind="props" icon class="reportdata-icon-btn reportdata-icon-btn--ghost mb-1"
                            variant="flat" @click="resetFilters" aria-label="Reset filters">
                            <v-icon size="24">mdi-sync</v-icon>
                          </v-btn>
                        </template>
                        <span>Reset to today</span>
                      </v-tooltip>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Action Row -->
            <div class="reportdata-action-row">
              <v-btn id="button-download-reportuseruse" class="reportdata-download-btn" height="50"
                @click="GetForDownload()" prepend-icon="mdi-download">
                {{ $t('DownloadReport') }}
              </v-btn>

              <div class="reportdata-stats">
                <div class="reportdata-stats-label">
                  <v-icon size="20" color="#C32E0E" class="mr-2">mdi-chart-box-outline</v-icon>
                  <span class="reportdata-stats-title">Report Statistics</span>
                </div>
                <v-chip size="large" color="primary" class="reportdata-stats-chip" variant="flat">
                  {{ dataTable.length }} Users
                </v-chip>
              </div>
            </div>
          </div>

          <!-- Data Table with Modern Styling -->
          <v-card class="reportdata-card-table">
            <div class="px-5 pt-5">
              <h2>{{ "User Usage Data" }}</h2>
            </div>

            <div class="reportdata-table-container">
              <v-data-table-server v-model:options="options" :items="dataTable" :items-per-page="options.itemsPerPage"
                :headers="headers" :items-length="totalTable" class="reportdata-table-modern" density="comfortable" hover :loading="loadingTable">

                <template #no-data>
                  <div class="d-flex justify-center align-center fixed" style="width: 100%">
                    <span style="color: #646464; font-weight: 400;">No Data Available</span>
                  </div>
                </template>

                <template #item="{ item }">
                  <tr class="reportdata-table-row">
                    <td class="text-center">
                      {{ item.no }}
                    </td>
                    <td>
                      <div class="reportdata-text-container">
                        <span v-for="(subItem, index) in item.title" :key="subItem" class="reportdata-text-standard">
                          {{ subArray(subItem, index, item.title.length) }}
                        </span>
                      </div>
                    </td>
                    <td class="text-center">
                      {{ substringLong(item.count) }}
                    </td>
                    <td class="text-center">
                      {{ substringLong(item.total_Tokens) }}
                    </td>
                    <td class="text-center">
                      {{ substringLong(item.totalImageGeneration) }}
                    </td>
                    <td class="text-center">
                      {{ substringLong(item.totalCachedTokens) }}
                    </td>
                    <td class="text-center">
                      {{ substringLong(item.totalReasoningTokens) }}
                    </td>
                  </tr>
                </template>

                <template v-slot:bottom>
                  <div class="table-bottom-group">
                    <div class="d-flex justify-space-between align-center px-4 py-2" style="width: 100%">
                      <div>Total results {{ totalTable }}</div>
        
                      <v-data-table-footer
                        :items-per-page-options="itemsPerPageOptions"
                        :page="options.page"
                        :length="pageCount"
                      />
                    </div>
                  </div>
                </template>
              </v-data-table-server>
            </div>
          </v-card>
        </v-card-text>
      </v-card>
    </v-container>

    <v-overlay :model-value="loading" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>
<script>
import dateUtils from '../plugins/date-utils';
import moment from 'moment';
import checkPermissionTwo from '@/api/function.js';
import LogTrace from '@/api/function.js';

export default {
  name: 'ReportUserUse',
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  mixins: [checkPermissionTwo, LogTrace],
  data() {
    const currentDate = new Date();
    return {
      loading: false,
      loadingTable: false,
      startDateFlag: false,
      endDateFlag: false,
      startDate: null,
      endDate: null,
      isSearch: false,
      selectedQuickRange: null,
      quickDateRanges: ['Today', 'Yesterday', 'Last 30 days', 'This month'],

      dataTable: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      totalTable: 0,
      isInitialized: false,
      itemsPerPageOptions: [
        { title: "10", value: 10 },
        { title: "25", value: 25 },
        { title: "50", value: 50 },
        { title: "100", value: 100 },
        { title: "All", value: -1 },
      ],

      // Date picker state management
      startPickerYear: currentDate.getFullYear(),
      startPickerMonth: currentDate.getMonth(),
      endPickerYear: currentDate.getFullYear(),
      endPickerMonth: currentDate.getMonth(),
      
      years: Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - i),
    };
  },
  computed: {
    headers() {
      return [
        {
          title: this.$t('no'),
          align: 'center',
          width: '8%',
          sortable: false,
        },
        {
          title: 'User ID',
          value: 'title',
          align: 'left',
          sortable: true,
          width: '25%',
        },
        {
          title: 'Total Questions',
          value: 'count',
          align: 'center',
          sortable: true,
          width: '12%',
        },
        {
          title: 'Total Tokens',
          value: 'total_Tokens',
          align: 'center',
          sortable: true,
          width: '13%',
        },
        {
          title: 'Image Generation',
          value: 'totalImageGeneration',
          align: 'center',
          sortable: true,
          width: '14%',
        },
        {
          title: 'Cached Tokens',
          value: 'totalCachedTokens',
          align: 'center',
          sortable: true,
          width: '14%',
        },
        {
          title: 'Reasoning Tokens',
          value: 'totalReasoningTokens',
          align: 'center',
          sortable: true,
          width: '14%',
        },
      ];
    },
    pageCount() {
      if (!this.totalTable) {
        return 0;
      }
      const perPage = this.options.itemsPerPage;
      if (!perPage || perPage === -1) {
        return 1;
      }
      return Math.ceil(this.totalTable / perPage);
    },
  },
  watch: {
    startDateFlag(newVal) {
      if (newVal) {
        if (this.startDate) {
          const date = new Date(this.startDate);
          this.startPickerYear = date.getFullYear();
          this.startPickerMonth = date.getMonth();
        } else {
          const today = new Date();
          this.startPickerYear = today.getFullYear();
          this.startPickerMonth = today.getMonth();
        }
      }
    },
    endDateFlag(newVal) {
      if (newVal) {
        if (this.endDate) {
          const date = new Date(this.endDate);
          this.endPickerYear = date.getFullYear();
          this.endPickerMonth = date.getMonth();
        } else {
          const today = new Date();
          this.endPickerYear = today.getFullYear();
          this.endPickerMonth = today.getMonth();
        }
      }
    },
    options() {
      if (!this.isInitialized) return;
      this.GetReport();
    },
  },
  methods: {
    getFormattedHeaderDate(year, month, selectedDate) {
      let date;
      
      if (selectedDate) {
        const selected = new Date(selectedDate);
        if (selected.getFullYear() === year && selected.getMonth() === month) {
          date = selected;
        } else {
          date = new Date(year, month, new Date().getDate());
        }
      } else {
        const today = new Date();
        if (today.getFullYear() === year && today.getMonth() === month) {
          date = today;
        } else {
          date = new Date(year, month, 1);
        }
      }
      
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },

    showWarningAlert(title, messageHtml) {
      const rawOkLabel = this.$t ? this.$t("OK") : "OK";
      const confirmLabel = rawOkLabel && rawOkLabel.toString().trim().length > 0
        ? rawOkLabel
        : "OK";

      return this.$swal.fire({
        title,
        html: messageHtml,
        icon: "warning",
        showCloseButton: true,
        focusConfirm: true,
        buttonsStyling: false,
        allowOutsideClick: false,
        confirmButtonText: confirmLabel,
        customClass: {
          popup: "swal-warning-popup",
          title: "swal-warning-title",
          htmlContainer: "swal-warning-body",
          actions: "swal-warning-actions",
          confirmButton: "swal-warning-confirm",
          closeButton: "swal-warning-close",
        },
      });
    },

    formatDate(dateInput) {
      if (!dateInput) {
        return '';
      }
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }

      const d = new Date(dateInput);
      if (isNaN(d.getTime())) {
        return dateInput.toString().substr(0, 10);
      }

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const date = `${year}-${month}-${day}`;
      return date;
    },
    clearStartDate() {
      this.startDate = null;
      this.selectedQuickRange = null;
    },
    clearEndDate() {
      this.endDate = null;
      this.selectedQuickRange = null;
    },
    Search() {
      let self = this;
      self.isSearch = true;
      self.GetReport();
    },

    GetReport() {
      let self = this;
      if (self.startDate != null && self.endDate != null) {
        if (self.isSearch) {
          self.options.page = 1;
          self.options.itemsPerPage = 10;
        }
        let { page, itemsPerPage, sortBy } = self.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === 'desc';
        }
        let totalPage = self.isSearch ? 10 : self.totalTable;
        let options = {
          page: self.isSearch ? 1 : page,
          perPage: itemsPerPage != -1 ? itemsPerPage : totalPage,
          sortDesc: sortOrder,
          sortBy: sortField
        };

        let temp = {
          StartDate: self.startDate,
          EndDate: self.endDate,
          OptionsDataTable: options,
        };

        self.$call.ReportUserUse(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              if (dataResponse.data.total > 0) {
                self.dataTable = dataResponse.data.listData;
                self.totalTable = dataResponse.data.total;
              }
              else {
                self.dataTable = [];
                self.totalTable = 0;
              }
            } else {
              self.dataTable = [];
              self.totalTable = 0;
            }
            self.isSearch = false;
            self.loadingTable = false;
          },
          (errorResource) => {
            self.loadingTable = false;
            console.error(errorResource);
          }
        );
      }
      else {
        self.showWarningAlert('Action Needed!', 'Date range is required. Please select both a start and end date.');
        self.loading = false;
        return;
      }
    },

    async GetForDownload() {
      let self = this;
      self.loading = true;

      if (self.startDate != null && self.endDate != null) {
        let { sortBy } = self.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === 'desc';
        }
        const perPage = 50;
        let options = {
          page: 1,
          perPage: perPage,
          sortDesc: sortOrder,
          sortBy: sortField
        };

        let temp = {
          StartDate: self.startDate,
          EndDate: self.endDate,
          OptionsDataTable: options,
        };

        await self.$call.ExportReportUserUse(
          temp,
          async (dataResponse) => {
            if (dataResponse) {
              let fileURL = window.URL.createObjectURL(new Blob([dataResponse]));
              let fileLink = document.createElement('a');

              fileLink.href = fileURL;
              // fileLink.setAttribute('href', fileURL);

              let fileNameShow = `ReportUserUse__${self.convertDateToString(new Date(), '')}.xlsx`;
              fileLink.setAttribute('download', fileNameShow);
              document.body.appendChild(fileLink);

              fileLink.click();
              document.body.removeChild(fileLink);
            }
            self.loading = false;
          },
          async (errorResource) => {
            self.loading = false
            console.error(errorResource);
          }
        );
      }
      else {
        self.showWarningAlert('Action Needed!', 'Download needs to filter both  a start and end date.');
        self.loading = false;
        return;
      }
    },

    convertDateToString(date, seperator = '/') {
      return dateUtils.getStringWithTimeFromDate(moment(date).toDate(), seperator);
    },

    convertDateToString2(date, seperator = '-') {
      return dateUtils.getStringFromDatePickFormatDatePick2(moment(date).toDate(), seperator);
    },

    substringLong(value) {
      if (value == null) {
        return '-';
      }
      if (value.length > 81) {
        return value.substring(0, 80) + '.....';
      }
      return value;
    },

    subArray(value, index, sum) {
      if (index == (sum - 1)) {
        return value;
      }
      else {
        return value + ", ";
      }
    },
    onQuickRangeChange(range) {
      if (!range) {
        return;
      }
      this.applyQuickRange(range);
    },
    applyQuickRange(range, triggerSearch = true) {
      const todayMoment = moment().startOf('day');
      let start = todayMoment.clone();
      let end = todayMoment.clone();

      switch (range) {
        case 'Yesterday':
          start = todayMoment.clone().subtract(1, 'day');
          end = start.clone();
          break;
        case 'Last 30 days':
          start = todayMoment.clone().subtract(29, 'day');
          end = todayMoment.clone();
          break;
        case 'This month':
          start = todayMoment.clone().startOf('month');
          end = todayMoment.clone();
          break;
        default:
          start = todayMoment.clone();
          end = todayMoment.clone();
          break;
      }

      this.startDate = this.formatDate(start.toDate());
      this.endDate = this.formatDate(end.toDate());
      this.selectedQuickRange = range;

      if (triggerSearch) {
        this.Search();
      }
    },
    resetFilters() {
      this.applyQuickRange('Today');
    },
  },
  mounted() {
    let self = this;
    var date = new Date();
    self.endDate = self.convertDateToString2(date);
    self.startDate = self.convertDateToString2(date);
    self.applyQuickRange('Today', false);
    self.GetReport();
    this.isInitialized = true;
  },
};
</script>

<style scoped>
.reportdata-card-main {
  overflow: hidden;
  background: #ffffff !important;
  box-shadow: unset !important;
}

.model-all-divider {
  width: 100% !important;
  border-color: #CCCCCC !important;
  height: 2px !important;
  opacity: 1 !important;
  margin: 5px 0px 25px 0px !important;
}

.reportdata-filters-wrapper {
  padding: 0px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.reportdata-filter-card {
  background: transparent;
  border: 1px solid #C9C9C9;
  border-radius: 18px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reportdata-filter-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}

.reportdata-filter-title {
  font-weight: 600;
  color: #171717 !important;
  letter-spacing: 0.2px;
}

.reportdata-field-date :deep(.v-field) {
  border-radius: 16px !important;
}

.date-style {
  border: 1px solid #c9c9c9;
  border-radius: 10px;
  height: 40px;
  background: #ffffff;
}

.date-style :deep(.v-field__outline),
.date-style :deep(.v-field__overlay) {
  display: none;
}

.date-style :deep(.v-field) {
  min-height: 40px;
}

.date-style :deep(.v-field__input) {
  padding: 8px 0 8px 4px;
  color: #1f1f1f;
  font-weight: 600;
  font-size: 14px;
  min-height: 40px;
}

.date-style :deep(.v-field__prepend-inner) {
  padding-top: 8px;
  padding-left: 4px;
}

.date-field-icon {
  margin-left: 6px;
}

.date-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #646464;
  font-weight: 700;
  font-size: 20px;
  padding-top: 20px;
}

.reportdata-quick-select {
  min-width: 200px;
  border-radius: 12px;
  border: 1px solid #C9C9C9;
  background: #ffffff;
  height: 42px;
}

.reportdata-quick-select :deep(.v-field__outline) {
  display: none;
}

.reportdata-quick-select :deep(.v-field__overlay) {
  display: none;
}

.reportdata-quick-select :deep(.v-select__selection-text) {
  font-weight: 600;
  color: #1c2c64;
}

.reportdata-quick-select :deep(.v-field__input) {
  padding-left: 4px;
}

.reportdata-quick-select :deep(.v-field__label) {
  font-weight: 600;
  color: #1c2c64;
}

.reportdata-quick-section {
  display: flex;
  flex-direction: column;
}

.reportdata-quick-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.reportdata-quick-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4b4b4b;
  font-weight: 600;
  font-size: 14px;
}

.reportdata-icon-btn {
  border-radius: 50%;
  height: 42px;
  width: 42px;
  min-width: 42px;
}

.reportdata-icon-btn--primary {
  background: linear-gradient(135deg, #fe6b3d 0%, #f3412f 100%) !important;
  color: #ffffff !important;
}

.reportdata-icon-btn--ghost {
  background: #ffffff !important;
  color: #171717 !important;
  border: 1px solid #d0d0d0 !important;
}

.reportdata-filter-divider {
  height: 50px;
  align-self: flex-end;
  border-color: #C9C9C9 !important;
  opacity: 1;
  margin-right: 10px;
}

.reportdata-action-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.reportdata-download-btn {
  background: linear-gradient(90deg, #C32E0E 0%, #F67760 100%);
  color: #ffffff;
  font-weight: 600;
  text-transform: none;
  border-radius: 16px;
  padding: 0 32px;
  letter-spacing: 0.4px;
}
.reportdata-download-btn:hover {
  background: linear-gradient(90deg, #fe6b3d 0%, #f3412f 100%);
}

.reportdata-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.reportdata-stats-label {
  display: flex;
  align-items: center;
}

.reportdata-stats-title {
  font-weight: 600 !important;
  color: #C32E0E !important;
}

.reportdata-stats-chip {
  background: #e9ecff !important;
  color: #3859D0 !important;
  font-weight: 600;
  border-radius: 999px;
}

.reportdata-table-container {
  border-radius: 18px;
  margin: 16px;
}

:deep(.v-data-table-server) {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

@media (max-width: 1200px) {
  /* .reportdata-filter-divider {
    display: none;
  } */

  .reportdata-quick-section {
    margin-top: 8px;
  }
}

/* Improve responsiveness for common 13" Mac display widths */
@media (max-width: 1400px) {
  /* .reportdata-filter-divider {
    display: none;
  } */
  .reportdata-quick-controls {
    gap: 12px;
  }
  .reportdata-quick-select {
    min-width: 180px;
  }
}

@media (max-width: 960px) {
  .reportdata-action-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .reportdata-quick-select {
    min-width: 100%;
  }

  .date-divider {
    display: none;
  }
}

:deep(thead) {
  background-color: #FFE4E1 !important;
  padding: 0 8px;
}

:deep(.v-overlay__content .quick-range-menu) {
  border-radius: 12px;
  padding: 4px;
}
.v-table--has-top :deep(.v-table__wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-left .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}
:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-center .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}
.reportdata-card-table {
  border: 1px solid #c9c9c9;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: none !important;
  margin: 5px 0px;
}

</style>

<!-- Global styles for dropdown menu (not scoped because menu is rendered in portal) -->
<style>
.quick-range-menu {
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
  background: #ffffff !important;
  min-width: 200px !important;
  overflow: hidden !important;
}

.quick-range-menu .v-list {
  padding: 4px !important;
  background: transparent !important;
}

.quick-range-menu .v-list-item {
  border-radius: 10px !important;
  margin: 2px 0 !important;
  min-height: 42px !important;
  padding: 8px 16px !important;
  position: relative !important;
}

.quick-range-menu .v-list-item:first-child {
  margin-top: 0 !important;
}

.quick-range-menu .v-list-item:last-child {
  margin-bottom: 0 !important;
}

/* Hide default overlay and use background instead */
.quick-range-menu .v-list-item__overlay {
  display: none !important;
}

/* Active/selected state - using background directly */
.quick-range-menu .v-list-item--active {
  background-color: #CDD5F3 !important;
}

/* Hover state */
.quick-range-menu .v-list-item:hover:not(.v-list-item--active) {
  background-color: #f5f5f5 !important;
}

.quick-range-menu .v-list-item-title {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: #171717 !important;
}

.quick-range-menu .v-list-item--active .v-list-item-title {
  color: #001763 !important;
  font-weight: 600 !important;
}

.quick-range-menu .v-list-item:hover .v-list-item-title {
  color: #001763 !important;
}
</style>
